import React, { useState, useEffect, useReducer, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";

import {
  Avatar,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Switch,
  Chip,
} from "@material-ui/core";

import {
  DeleteOutline,
  Edit,
  FileCopyOutlined,
  Refresh,
  Code as CodeIcon,
} from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ApiRestModal from "../../components/ApiRestModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import api from "../../services/api";
import toastUtils from "../../utils/toastUtils";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD":
      return action.payload;
    case "UPSERT": {
      const i = state.findIndex((x) => x.id === action.payload.id);
      if (i !== -1) {
        const next = [...state];
        next[i] = action.payload;
        return next;
      }
      return [action.payload, ...state];
    }
    case "DELETE":
      return state.filter((x) => x.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    margin: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  iconAvatar: {
    width: 40,
    height: 40,
    backgroundColor: "#9c27b0",
  },
  tokenChip: {
    fontFamily: "monospace",
    cursor: "pointer",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  resourceChip: {
    margin: 2,
    fontSize: 11,
    height: 22,
  },
  helpBox: {
    background: "#f0f7ff",
    borderLeft: "4px solid #2563eb",
    padding: theme.spacing(1.5, 2),
    margin: theme.spacing(1, 1, 2),
    borderRadius: 4,
    fontSize: 13,
    color: "#1e3a8a",
  },
}));

const ApiRest = ({ embedded = false }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [integrations, dispatch] = useReducer(reducer, []);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);

  const apiBaseUrl = window.location.origin.includes("localhost")
    ? "http://localhost:8080"
    : window.location.origin.replace(":3000", ":8080");

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api-rest-integrations");
      dispatch({ type: "LOAD", payload: data });
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenNew = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    setSelected(item);
    setModalOpen(true);
  };

  const handleClose = (saved) => {
    setModalOpen(false);
    setSelected(null);
    if (saved) dispatch({ type: "UPSERT", payload: saved });
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api-rest-integrations/${id}`);
      dispatch({ type: "DELETE", payload: id });
      toastUtils.success("Integração removida", "Removida");
    } catch (err) {
      toastError(err);
    }
    setDeletingItem(null);
  };

  const handleToggleActive = async (item) => {
    try {
      const { data } = await api.put(`/api-rest-integrations/${item.id}`, {
        active: !item.active,
      });
      dispatch({ type: "UPSERT", payload: data });
    } catch (err) {
      toastError(err);
    }
  };

  const handleRegenerateToken = async (item) => {
    if (
      !window.confirm(
        "Tem certeza? O token atual deixará de funcionar imediatamente."
      )
    )
      return;
    try {
      const { data } = await api.post(
        `/api-rest-integrations/${item.id}/regenerate-token`
      );
      dispatch({ type: "UPSERT", payload: data });
      toastUtils.success("Novo token gerado", "Token atualizado");
    } catch (err) {
      toastError(err);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toastUtils.success(`${label} copiado`, "Copiado");
  };

  const buildUrl = (item, resource = "stats") =>
    `${apiBaseUrl}/api/v1/external/${item.token}/${resource}`;

  const filtered = integrations.filter((it) =>
    it.name.toLowerCase().includes(searchParam.toLowerCase())
  );

  if (user.profile === "user") return <ForbiddenPage />;

  const content = (
    <>
      {!embedded && (
        <MainHeader>
          <Title>API Rest ({integrations.length})</Title>
          <MainHeaderButtonsWrapper>
            <TextField
              placeholder="Pesquisar..."
              type="search"
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="secondary" />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" color="primary" onClick={handleOpenNew}>
              + Nova API
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>
      )}

      {embedded && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 16px",
            gap: 12,
          }}
        >
          <span style={{ fontSize: 13, color: "#6b7280" }}>
            {integrations.length} integração(ões) cadastrada(s)
          </span>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <TextField
              placeholder="Pesquisar..."
              type="search"
              size="small"
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="secondary" />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" color="primary" onClick={handleOpenNew}>
              + Nova API
            </Button>
          </div>
        </div>
      )}

      <div className={classes.helpBox}>
        💡 Crie tokens de acesso para sistemas externos consumirem dados do CRM
        (n8n, Zapier, dashboards, WordPress, etc.). Para cada integração você
        escolhe quais recursos e quais campos liberar.
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"></TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Recursos liberados</TableCell>
              <TableCell>Token</TableCell>
              <TableCell align="center">Usos</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Avatar className={classes.iconAvatar}>
                    <CodeIcon />
                  </Avatar>
                </TableCell>
                <TableCell>
                  <strong>{item.name}</strong>
                </TableCell>
                <TableCell>
                  {(item.allowedResources || []).map((r) => (
                    <Chip
                      key={r}
                      label={r}
                      size="small"
                      className={classes.resourceChip}
                    />
                  ))}
                  {(!item.allowedResources ||
                    item.allowedResources.length === 0) && (
                    <span style={{ color: "#999" }}>nenhum</span>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Clique para copiar o token">
                    <Chip
                      label={item.token}
                      size="small"
                      onClick={() => copyToClipboard(item.token, "Token")}
                      className={classes.tokenChip}
                      icon={<FileCopyOutlined style={{ fontSize: 14 }} />}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="center">{item.requestCount || 0}</TableCell>
                <TableCell align="center">
                  <Switch
                    checked={!!item.active}
                    onChange={() => handleToggleActive(item)}
                    color="primary"
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Copiar URL exemplo (stats)">
                    <IconButton
                      size="small"
                      onClick={() =>
                        copyToClipboard(buildUrl(item, "stats"), "URL")
                      }
                    >
                      <FileCopyOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Gerar novo token">
                    <IconButton
                      size="small"
                      onClick={() => handleRegenerateToken(item)}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleEdit(item)}>
                      <Edit fontSize="small" color="secondary" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeletingItem(item);
                        setConfirmOpen(true);
                      }}
                    >
                      <DeleteOutline fontSize="small" color="secondary" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={7} />}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ padding: 30 }}>
                  Nenhuma integração ainda. Clique em "+ Nova API" para criar.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </>
  );

  return (
    <>
      <ConfirmationModal
        title={
          deletingItem ? `Excluir integração "${deletingItem.name}"?` : ""
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => handleDelete(deletingItem.id)}
      >
        Esta ação não pode ser desfeita. O token deixará de funcionar.
      </ConfirmationModal>

      <ApiRestModal
        open={modalOpen}
        onClose={handleClose}
        integration={selected}
        apiBaseUrl={apiBaseUrl}
      />

      {embedded ? content : <MainContainer>{content}</MainContainer>}
    </>
  );
};

export default ApiRest;
